<script setup lang="ts">
import { linkExternal } from '@/components/icons/icons.ts';
import IdentifierLabel from '@/components/IdentifierLabel/IdentifierLabel.vue';
import { routenames } from '@/router/routenames.ts';
import { computed } from 'vue';
import { RouteLocationRaw } from 'vue-router';

type EntityLinkType = 'jobConfiguration' | 'qaEquipment' | 'medicalSystem' | 'protocol';

const props = withDefaults(
  defineProps<{
    id?: string;
    name: string;
    iuid?: string | null;
    link?: boolean;
    openInTab?: boolean;
    type: EntityLinkType;
  }>(),
  {
    id: undefined,
    iuid: undefined,
    link: true,
    openInTab: false,
  },
);

defineEmits<{
  click: [];
}>();

const routeName = computed(() => {
  switch (props.type) {
    case 'jobConfiguration':
      return routenames.jobConfigurations.details;
    case 'qaEquipment':
      return routenames.qaEquipment.details;
    case 'medicalSystem':
      return routenames.medicalSystems.edit;
    case 'protocol':
      return routenames.protocols.details;
    default:
      throw new Error(`Unknown EntityLink type ${props.type}`);
  }
});

const toLocation = computed(() =>
  props.link
    ? ({
        name: routeName.value,
        params: { id: props.id },
      } satisfies RouteLocationRaw)
    : undefined,
);
</script>

<template>
  <v-btn
    color="secondary"
    class="entity-link px-0 py-0 opacity-100"
    variant="plain"
    density="compact"
    :disabled="!toLocation"
    :appendIcon="toLocation ? linkExternal : undefined"
    :to="toLocation"
    :target="openInTab ? '_blank' : undefined"
    @click.stop="$emit('click')"
  >
    <IdentifierLabel :label="name" :identifier="iuid" :link="!!toLocation" />
  </v-btn>
</template>

<style lang="scss" scoped>
.entity-link {
  letter-spacing: unset;
  color: rgb(var(--v-theme-secondary)) !important;
  &:visited {
    color: rgb(var(--v-theme-secondary-lighten-1)) !important;
  }
  &:hover {
    color: rgb(var(--v-theme-secondary-darken-2)) !important;
  }
}
</style>
